import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from '@tanstack/react-router';

import { analytics } from '@/analytics/analytics-service';
import {
  equipmentIdAtom,
  identifiersAtom,
  nextEquipmentIdAtom,
  prevEquipmentIdAtom,
} from '@/features/equipment-browser/equipment-details/state/selected-equipment.state';
import {
  drawerIsOpenAtom,
  drawerTypeAtom,
} from '@/features/equipment-browser/equipment-drawer-loader/state/drawer.state';
import { EquipmentIdentifiers } from '@/features/equipment-browser/types';
import { EquipmentSearch } from '@/routes/$org/$agent/equipment';

const SelectedEquipmentState = {
  identifiersAtom,
  equipmentIdAtom,
  nextEquipmentIdAtom,
  prevEquipmentIdAtom,
};

export type DeviceDetailsAnalyticsAttributes = {
  'Device Kind'?: string;
  'Device Alias'?: string;
};

export const useSelectEquipment = () => {
  const [identifiers, setIdentifiers] = useAtom(SelectedEquipmentState.identifiersAtom);
  const setEquipmentId = useSetAtom(SelectedEquipmentState.equipmentIdAtom);
  const nextEquipmentId = useAtomValue(SelectedEquipmentState.nextEquipmentIdAtom);
  const prevEquipmentId = useAtomValue(SelectedEquipmentState.prevEquipmentIdAtom);
  const isDrawerOpen = useAtomValue(drawerIsOpenAtom);
  const drawerType = useAtomValue(drawerTypeAtom);
  const setDrawerIsOpen = useSetAtom(drawerIsOpenAtom);
  const setDrawerType = useSetAtom(drawerTypeAtom);
  const navigate = useNavigate();

  /**
   * Update the search params with the new identifiers
   * This will update the url in the browser.
   *
   * @param newIdentifiers - The new identifiers to update the search params with
   */
  const updateSearchParams = async (newIdentifiers: EquipmentIdentifiers) => {
    const searchParams: EquipmentSearch = {
      viewId: newIdentifiers.viewId,
      sectionId: newIdentifiers.sectionId,
      equipmentId: newIdentifiers.equipmentId,
    };
    await navigate({
      search: searchParams as any,
      replace: true, // replace only the url params, no route refresh
    });
  };

  /**
   * Select the equipment and update the identifiers and search params
   *
   * @param equipmentId - The equipment id to select
   * @param sectionId - The section id to select
   * @param viewId - The view id to select
   * @returns void
   */
  const selectEquipmentAction = async (
    equipmentId?: string | undefined,
    sectionId?: string | undefined,
    viewId?: string | undefined,
    analyticsAttributes?: DeviceDetailsAnalyticsAttributes
  ) => {
    const newIdentifiers = {
      equipmentId: equipmentId ?? identifiers?.equipmentId,
      sectionId: sectionId ?? identifiers?.sectionId,
      viewId: viewId ?? identifiers?.viewId,
    };

    setEquipmentId(equipmentId);
    setIdentifiers(newIdentifiers);
    if (equipmentId) {
      if (!isDrawerOpen || drawerType !== 'details') {
        analytics.track('Device Details Opened', {
          ...analyticsAttributes,
        });
      }
      setDrawerType('details');
      setDrawerIsOpen(true);
    }
    updateSearchParams(newIdentifiers);
  };

  /**
   * Clear the equipment and update the identifiers and search params
   * Retain the view and section ids when closing the drawer
   *
   * @returns void
   */
  const clearEquipmentAction = async () => {
    const newIdentifiers = {
      viewId: identifiers?.viewId,
      sectionId: identifiers?.sectionId,
      equipmentId: undefined,
    };

    setEquipmentId(undefined);
    setIdentifiers(newIdentifiers);
    setDrawerIsOpen(false);
    setDrawerType(undefined);
    updateSearchParams(newIdentifiers);
  };

  /**
   * Select the next equipment in the equipment pool
   *
   * @returns void
   */
  const selectNextAction = async () => {
    if (!nextEquipmentId) return;
    selectEquipmentAction(nextEquipmentId, identifiers?.sectionId, identifiers?.viewId);
  };

  /**
   * Select the previous equipment in the equipment pool
   *
   * @returns void
   */
  const selectPreviousAction = async () => {
    if (!prevEquipmentId) return;
    selectEquipmentAction(prevEquipmentId, identifiers?.sectionId, identifiers?.viewId);
  };

  return {
    clearEquipmentAction,
    selectEquipmentAction,
    selectNextAction,
    selectPreviousAction,
    updateSearchParams,
  };
};
